import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Card, Input, Logo } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useAuth } from "../lib/auth.js";
import { useToast } from "../lib/toast.js";

/**
 * Dual-kind sign-in page (staff + supplier portal). One form, one set of
 * credentials. We try the staff credentials route first; on an identical
 * 401 (`/v1/auth/staff/sign-in` and `/v1/auth/supplier/sign-in` return the
 * same envelope, so there is no account-kind enumeration) we transparently
 * retry against the supplier route. Any non-401 error from the staff attempt
 * rethrows immediately.
 *
 * After a successful sign-in we `refresh()` the auth context and branch on
 * the returned MeResponse.kind: suppliers land on the portal PO queue
 * (/supplier/orders); staff land on /products as before.
 */
export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();

  // An already-authenticated supplier landing on /login (e.g. via a bookmark
  // or the catch-all redirect) goes straight to their PO queue rather than
  // seeing the sign-in form. Staff are left on the form as before — the
  // pre-existing behaviour — so this is the smallest additive change.
  if (me?.kind === "supplier") {
    return <Navigate to="/supplier/orders" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      try {
        await api.post("/v1/auth/staff/sign-in", { email, password });
      } catch (staffErr) {
        // Staff 401 → these might be supplier-portal credentials. Both routes
        // return the same 401 envelope, so retrying does not leak which kind
        // of account exists. Any other staff error (5xx, network) rethrows.
        if (staffErr instanceof ApiError && staffErr.status === 401) {
          await api.post("/v1/auth/supplier/sign-in", { email, password });
        } else {
          throw staffErr;
        }
      }
      const me = await refresh();
      notify("success", "Signed in");
      navigate(me?.kind === "supplier" ? "/supplier/orders" : "/products");
    } catch (err) {
      if (err instanceof ApiError && err.problem) {
        notifyProblem(err.problem);
      } else {
        notify(
          "error",
          err instanceof Error ? err.message : "Sign-in failed",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="flex min-h-screen items-center justify-center bg-paper-warm p-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex flex-col items-center gap-4">
          <Logo size="lg" />
          <h1 className="font-serif text-display-sm text-ink-900">
            WMart admin
          </h1>
        </div>
        <form
          onSubmit={onSubmit}
          className="mt-8 flex flex-col gap-4"
        >
          <Input
            type="email"
            label="Email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            label="Password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            full
            disabled={busy}
            loading={busy}
          >
            Sign in
          </Button>
        </form>
      </Card>
    </main>
  );
}
